import { Children, forwardRef } from 'react'
import { resolveComponentVariant } from '../componentContract'
import styles from './Forms.module.css'

const sizes = ['compact', 'default']
const controlClasses = (kind, size, className) => {
  const resolvedSize = resolveComponentVariant(`${kind} size`, size, sizes, 'default')
  return [styles.control, styles[kind], resolvedSize === 'compact' ? styles.compact : '', className]
    .filter(Boolean).join(' ')
}

// `actions` puts controls inside the input's right edge (a clear X, a show
// password toggle, a colour picker) and keeps the text clear of them. Every
// other prop still lands on the <input>, so Field's id and aria wiring reach it.
// Pass a list, not a fragment (each entry is one action), and pass it (empty
// if need be) for as long as the field can have actions: switching between a
// bare input and one with actions remounts the input and drops focus.
export const Input = forwardRef(function Input({ size = 'default', className = '', actions, ...props }, ref) {
  const input = <input {...props} ref={ref} className={controlClasses('input', size, className)} />
  return actions === undefined ? input : <InputActions actions={actions}>{input}</InputActions>
})

// The layout behind Input's `actions`, for an <input> styled outside the
// design system (PasswordInput on the auth pages).
export function InputActions({ actions, children }) {
  const count = Children.toArray(actions).length
  return (
    <div className={styles.inputGroup} style={{ '--wf-input-action-count': count }}>
      {children}
      {count ? <div className={styles.inputActions}>{actions}</div> : null}
    </div>
  )
}

export const Textarea = forwardRef(function Textarea({ size = 'default', className = '', ...props }, ref) {
  return <textarea {...props} ref={ref} className={controlClasses('textarea', size, className)} />
})

export const Select = forwardRef(function Select({ size = 'default', className = '', children, ...props }, ref) {
  return <select {...props} ref={ref} className={controlClasses('select', size, className)}>{children}</select>
})

function Choice({ type, label, description, className = '', disabled = false, ...props }) {
  return (
    <label className={`${styles.choice} ${disabled ? styles.choiceDisabled : ''} ${className}`.trim()}>
      <input {...props} type={type} disabled={disabled} />
      <span className={styles.choiceText}>
        <span className={styles.choiceLabel}>{label}</span>
        {description ? <span className={styles.choiceDescription}>{description}</span> : null}
      </span>
    </label>
  )
}

export function Checkbox(props) { return <Choice {...props} type="checkbox" /> }
export function Radio(props) { return <Choice {...props} type="radio" /> }

// `readOnly`: looks disabled and can't be toggled, but stays in the tab order
// (aria-disabled), so a view-only form can still be read from the keyboard.
export function Switch({ checked, onChange, label, description, disabled = false, readOnly = false, size = 'default', className = '', role = 'switch', ...props }) {
  // A label on its own lines up with the middle of the track; with a
  // description under it, with the track's top.
  const classes = [
    styles.choice,
    description ? '' : styles.choiceCentered,
    size === 'compact' ? styles.switchCompact : '',
    disabled || readOnly ? styles.choiceDisabled : '',
    className,
  ].filter(Boolean).join(' ')
  return (
    <label className={classes}>
      <input
        {...props}
        className={styles.switchInput}
        type="checkbox"
        role={role}
        checked={checked}
        aria-disabled={readOnly || props['aria-disabled'] || undefined}
        onChange={(event) => { if (!readOnly) onChange?.(event.target.checked, event) }}
        disabled={disabled}
      />
      <span className={styles.switchTrack} aria-hidden="true" />
      <span className={styles.choiceText}>
        <span className={styles.choiceLabel}>{label}</span>
        {description ? <span className={styles.choiceDescription}>{description}</span> : null}
      </span>
    </label>
  )
}
